/**
 * Craft My Bundle data contract: pure functions, no DOM, so they can be tested and reused by any UI.
 *
 * - requiredQuantity: exact number of products the shopper selects in a step.
 * - qty: total units of each selected product; every unit gets its own variant id (variantIds.length === qty).
 * - variantGIDs: exact allow-list of variants the merchant chose (absent = legacy config).
 * Prices are never sent to the cart: the Cart Transform stays the pricing authority.
 * bundlePrice only mirrors the app's configured price for display.
 */
(() => {
  const STRATEGIES = ['fixed_total', 'variable', 'fixed_components'];

  const positiveInteger = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : 1;
  };

  const numericId = (gid) => String(gid ?? '').split('/').pop();

  /** Supports both step formats: legacy `[items]` and current `{requiredQuantity, items}`. */
  const normalizeSteps = (bundle) =>
    (bundle?.steps ?? []).map((step) => {
      const isCurrentFormat = !Array.isArray(step);
      return {
        requiredQuantity: positiveInteger(isCurrentFormat ? step.requiredQuantity : 1),
        items: (isCurrentFormat ? step.items : step).map((item) => ({
          ...item,
          qty: positiveInteger(item.qty),
          variantGIDs: Array.isArray(item.variantGIDs) ? item.variantGIDs : null,
        })),
      };
    });

  const isValidBundle = (bundle, steps) =>
    STRATEGIES.includes(bundle?.strategy) &&
    Boolean(bundle?.containerVariantGID) &&
    steps.length > 0 &&
    steps.every((step) => step.items.length > 0);

  /** Variants the shopper may pick for an item: available and inside the merchant's allow-list. */
  const eligibleVariants = (item, product) => {
    if (!product) return [];
    const available = product.variants.filter((variant) => variant.available);
    if (!item.variantGIDs) return available;
    const allowed = new Set(item.variantGIDs.map(numericId));
    return available.filter((variant) => allowed.has(String(variant.id)));
  };

  /** Indexes of steps with fewer in-stock options than requiredQuantity: the shopper could never complete them. */
  const blockedSteps = (steps, products) =>
    steps.flatMap((step, index) =>
      step.items.filter((item) => eligibleVariants(item, products.get(item.handle)).length > 0).length < step.requiredQuantity ? [index] : []
    );

  /**
   * selections: one array per step of `{ itemIndex, variantIds }`, where `variantIds` holds one variant
   * id per unit of the item (length === item.qty). Returns error codes; empty means valid.
   */
  const validateSelection = (steps, selections, products) => {
    const errors = [];
    steps.forEach((step, stepIndex) => {
      const chosen = selections[stepIndex] ?? [];
      if (chosen.length !== step.requiredQuantity) errors.push(`step-${stepIndex}-count`);
      chosen.forEach(({ itemIndex, variantIds }) => {
        const item = step.items[itemIndex];
        const eligible = item ? eligibleVariants(item, products.get(item.handle)) : [];
        const ids = Array.isArray(variantIds) ? variantIds : [];
        const valid =
          item && ids.length === item.qty && ids.every((id) => eligible.some((variant) => String(variant.id) === String(id)));
        if (!valid) errors.push(`step-${stepIndex}-variant-${itemIndex}`);
      });
    });
    return errors;
  };

  /** Default unit assignment: unit i takes eligible variant `i % eligible.length`. Returns string ids. */
  const defaultUnitVariantIds = (qty, eligible) =>
    eligible.length ? Array.from({ length: qty }, (_, index) => String(eligible[index % eligible.length].id)) : [];

  /** Expands `{variantId: count}` (object or Map) into unit ids in eligible-variant order; unknown ids are ignored. */
  const expandUnitCounts = (counts, eligible) => {
    const entries = typeof counts?.get === 'function' ? Object.fromEntries(counts) : (counts ?? {});
    return eligible.flatMap((variant) => Array(Math.max(0, Math.floor(Number(entries[variant.id]) || 0))).fill(String(variant.id)));
  };

  /** An item pick is complete only when it holds exactly `item.qty` unit ids. */
  const isItemComplete = (item, variantIds) => Array.isArray(variantIds) && variantIds.length === item.qty;

  /**
   * Picks still missing across all steps. `picks` is one array per step of `{ itemIndex, variantIds }`;
   * only complete picks (exactly item.qty units) reduce the count.
   */
  const countMissing = (steps, picks) =>
    steps.reduce((total, step, stepIndex) => {
      const done = (picks[stepIndex] ?? []).filter(({ itemIndex, variantIds }) => step.items[itemIndex] && isItemComplete(step.items[itemIndex], variantIds)).length;
      return total + Math.max(0, step.requiredQuantity - done);
    }, 0);

  /** Groups unit variant ids into `[{ variantId, count }]`, keeping first-seen order. */
  const groupUnits = (variantIds) => {
    const counts = new Map();
    variantIds.forEach((id) => counts.set(String(id), (counts.get(String(id)) ?? 0) + 1));
    return [...counts].map(([variantId, count]) => ({ variantId, count }));
  };

  /**
   * Ajax Cart lines. Attribute names match the Cart Transform. `selected` is `[{ item, variantIds }]`;
   * each item's units are grouped per distinct variant so the total quantity stays `item.qty`.
   */
  const createCartLines = (bundle, selected, instanceId) => {
    const properties = { _bundle_parent_id: bundle.containerVariantGID, _bundle_instance_id: instanceId };
    if (bundle.strategy === 'fixed_components') {
      return [
        {
          id: Number(numericId(bundle.containerVariantGID)),
          quantity: 1,
          properties: {
            ...properties,
            _bundle_items: JSON.stringify(
              selected.flatMap(({ variantIds }) =>
                groupUnits(variantIds).map(({ variantId, count }) => ({
                  variant_id: `gid://shopify/ProductVariant/${variantId}`,
                  quantity: count,
                }))
              )
            ),
          },
        },
      ];
    }
    return selected.flatMap(({ variantIds }) =>
      groupUnits(variantIds).map(({ variantId, count }) => ({ id: Number(variantId), quantity: count, properties: { ...properties } }))
    );
  };

  /**
   * Display price from the app's registry entry (not the admin product price). `selected` is
   * `[{ item, variantIds }]`. Returns a number, or null when there is nothing to show yet.
   */
  const bundlePrice = (bundle, selected) => {
    const positive = (value) => (Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value) : null);
    if (bundle?.strategy === 'fixed_total') return positive(bundle.total_price);
    const units = selected.flatMap(({ variantIds }) => variantIds.map(String));
    if (!units.length) return null;
    if (bundle?.strategy === 'variable') {
      const unit = positive(bundle.unit_price);
      return unit === null ? null : unit * units.length;
    }
    if (bundle?.strategy === 'fixed_components') {
      const prices = units.map((id) => positive(bundle.component_prices?.[`gid://shopify/ProductVariant/${id}`]));
      return prices.some((price) => price === null) ? null : prices.reduce((sum, price) => sum + price, 0);
    }
    return null;
  };

  window.BundleContract = Object.freeze({
    STRATEGIES,
    positiveInteger,
    numericId,
    normalizeSteps,
    isValidBundle,
    eligibleVariants,
    validateSelection,
    blockedSteps,
    defaultUnitVariantIds,
    expandUnitCounts,
    isItemComplete,
    countMissing,
    createCartLines,
    bundlePrice,
  });
})();
